import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import './productImageZoom.css';

/* Line icons drawn inline: master has no shared icon set yet, and both are decoration beside words or an aria-label. */
const iconProps = { viewBox: '0 0 24 24', className: 'ff-icon', 'aria-hidden': true, focusable: false } as const;
const CloseIcon = () => (
  <svg {...iconProps}>
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);
const ExternalIcon = () => (
  <svg {...iconProps}>
    <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
  </svg>
);

export interface ProductImageZoomProps {
  /** The image the thumbnail shows; the popup shows the same file, large. */
  src: string;
  /** Names the product: the button reads "View a larger image of <label>", and it heads the popup. */
  label: string;
  /** The thumbnail exactly as the screen draws it. */
  children: ReactNode;
  /** Classes for the button that wraps the thumbnail, for the screen's own placement rules. */
  className?: string;
  /** Runs once the popup has closed and focus is back on the thumbnail, so a scan desk can take the cursor home. */
  onClosed?: () => void;
}

/** Only a web address becomes a link out; anything else in the image field is shown but never followed. */
const webHref = (url: string): string | null => (/^https?:\/\//i.test(url) ? url : null);

function ProductImageZoomDialog({ src, label, onClose }: { src: string; label: string; onClose: () => void }) {
  const ref = useModalDialog(onClose);
  const href = webHref(src);
  // Portalled to <body>: inside the thumb box the screen's own `.thumb img` rules (a 34px cover crop, the 1.15 zoom,
  // a locked table's grey) would reach the large image too. React still carries its events up the component tree.
  return createPortal(
    <dialog
      ref={ref}
      className="ff-img-zoom"
      aria-label={label}
      // The popup sits inside table rows and other dialogs in React's tree; their handlers never see clicks inside it.
      // A click on the dialog element itself is a click on the dimmed backdrop around the panel.
      onClick={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="ff-img-zoom-panel">
        <header>
          <b title={label}>{label}</b>
          <button type="button" className="x" aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </button>
        </header>
        <div className="ff-img-zoom-body">
          <img src={src} alt={label} />
        </div>
        {href && (
          <footer>
            <a href={href} target="_blank" rel="noreferrer">
              <ExternalIcon />
              Open original
            </a>
          </footer>
        )}
      </div>
    </dialog>,
    document.body,
  );
}

/**
 * A product thumbnail that opens its image large [SSD-7518]. At 32-48px two similar products look alike, so a click
 * shows the image over the screen; Esc, the ✕ or a click outside it closes the popup and focus returns to the thumbnail.
 * The popup is a native modal `<dialog>`, so it also stacks over the screen's own dialogs (Purchase order lookup,
 * Send to Unrecognized Pool) and Esc closes only the popup. The thumbnail's own click still reaches the row, as before.
 */
export function ProductImageZoom({ src, label, children, className, onClosed }: ProductImageZoomProps) {
  const [open, setOpen] = useState(false);
  const wasOpen = useRef(false);
  const closed = useRef(onClosed);
  closed.current = onClosed;

  // The dialog's cleanup hands focus back to the thumbnail first; this runs after it, so the screen has the last word.
  useEffect(() => {
    if (wasOpen.current && !open) {
      closed.current?.();
    }
    wasOpen.current = open;
  }, [open]);

  return (
    <>
      <button
        type="button"
        className={['ff-img-zoom-btn', className].filter(Boolean).join(' ')}
        aria-label={`View a larger image of ${label}`}
        title="Click to see the image large"
        onClick={() => setOpen(true)}
      >
        {children}
      </button>
      {open && <ProductImageZoomDialog src={src} label={label} onClose={() => setOpen(false)} />}
    </>
  );
}
